'use client';

import classnames from 'classnames';
import { useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import styles from 'molecules/Form/textinput/textinput.module.scss';

import type { ChangeEvent, FC, FocusEvent } from 'react';

// Component props definition
interface TextareaProps {
  formStateProps?: {
    checked?: boolean;
    multiple?: boolean;
    name?: string;
    onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => void;
    onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    value?: string;
  };
  disabled?: boolean;
  id: string; // Used as input ID and label "for"
  label: string;
  maxLength?: number;
  message?: string; // Error/help text shown below the input
  noAutoSize?: boolean; // switch between using autoresize and vanilla textarea
  required?: boolean; // If true, adds required indicators
  valid?: boolean; // Controls error styling
  validate?: (fieldName: string, value: string, required: boolean) => void;
  validateOnBlur?: (fieldName: string, value: string, required: boolean) => void; // Optional blur validation callback
  validateOnChange?: (fieldName: string, value: string, required: boolean) => void; // Optional change validation callback
  className?: string; // Optional custom wrapper class
}

const Textarea: FC<TextareaProps> = ({
  formStateProps = {},
  disabled,
  id,
  label,
  maxLength,
  message = 'This field is required.',
  required = false,
  valid = true,
  noAutoSize = false,
  className,
  validateOnBlur,
  validateOnChange,
}) => {
  // Local state for input value and focus tracking
  const [value, setValue] = useState<string>(formStateProps.value || '');
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // Trigger blur validation
  const handleBlur = () => {
    setIsFocused(false);
    validateOnBlur?.(id, value, required);
  };

  // Destructure onChange so we can call it after internal logic
  const { onChange: outerOnChange, ...passFormStateProps } = formStateProps;

  // Main change handler for textarea input
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    let inputValue = e.target.value;

    // Special handling for NPI Number input
    if (id === 'NPI Number') {
      inputValue = inputValue.replace(/[^0-9]/g, '');

      if (inputValue.length > 10) {
        inputValue = inputValue.slice(0, 10);
      }
    }

    // Update local state
    setValue(inputValue);

    // Trigger optional validation callback
    validateOnChange?.(id, inputValue, required);

    // Call parent onChange if passed
    if (outerOnChange) {
      outerOnChange(e);
    }
  };

  // Wrapper div classnames: handles error, required, float label, and disabled styles
  const wrapperClass = classnames(styles.wrapper, {
    [styles.floated]: value,
    [styles.error]: !valid,
    [styles.required]: required,
    [styles.disabled]: disabled,
    [className || '']: className,
  });

  // Label class: adds "focused" style when input is focused or has content
  const labelClass = classnames(styles.label, {
    [styles.focused]: isFocused || value,
  });

  return (
    <div className={`${wrapperClass} ${id === 'NPI Number' ? styles.npiTextArea : ''}`}>
      {/* Floating label */}
      <label className={labelClass} htmlFor={id}>
        {label}
      </label>

      {/* Resizing textarea component */}
      {!noAutoSize ? (
        <TextareaAutosize
          {...passFormStateProps}
          className={styles.input}
          id={id}
          aria-describedby={!valid ? `error-${id}` : undefined}
          aria-invalid={!valid}
          aria-required={required}
          disabled={disabled}
          maxLength={maxLength}
          onBlur={handleBlur}
          onChange={handleChange}
          value={value}
        />
      ) : (
        <textarea
          {...passFormStateProps}
          className={styles.input}
          id={id}
          aria-describedby={!valid ? `error-${id}` : undefined}
          aria-invalid={!valid}
          aria-required={required}
          disabled={disabled}
          maxLength={maxLength}
          onBlur={handleBlur}
          onChange={handleChange}
          value={value}
        />
      )}

      {/* Error or help message */}
      <span id={`error-${id}`} className={styles.errorMessage} aria-hidden={valid}>
        {message}
      </span>
    </div>
  );
};

export default Textarea;
